import { queryClient } from "./api/queryClient";
import { Audience } from "./components/Access/audience";
import { PageDataContext, prefetchPageData } from "./pageData";

/**
 * Warm a nav destination while the pointer is still on the link.
 *
 * Clicking a nav link pays two costs before anything renders: fetching the page's lazy chunk, and
 * then its data. Hovering is dead time that covers both, so on hover we import the destination
 * module — which is the chunk fetch — and then fetch whatever that module declares in its
 * `pageQueries` export (see src/pageData.ts, which route loaders share).
 */

type Loader = () => Promise<unknown>;

/** Destinations that resolve to the same page for everyone. */
const SHARED: Record<string, Loader> = {
  "/notifications": () => import("./components/Notifications/Notifications"),
  "/calendar": () => import("./components/Calendar/UserCalendar"),
  "/infusion-calendar": () => import("./components/Calendar/Calendar"),
  "/provider-calendar": () => import("./pages/ProviderCalendar"),
  "/billing": () => import("./pages/BillingPage"),
  "/referrals": () => import("./components/Referrals/ReferralsPage"),
  "/locations": () => import("./components/Locations/Locations"),
  "/imaging-centers": () => import("./components/ImagingCenters/ImagingCenters"),
  "/users": () => import("./components/Users/UsersPage"),
  "/launch-waitlist": () => import("./pages/LaunchWaitlistPage"),
  "/external": () => import("./components/ExternalProviders/ExternalProviders"),
  "/site-settings": () => import("./components/SiteSettings/SiteSettings"),
  "/audit": () => import("./components/Audit/Audit"),
  "/treatment": () => import("./components/Treatment/Treatment"),
  "/mris": () => import("./components/Dashboard/Internal/MrisNeedingApprovalList"),
  "/help": () => import("./pages/HelpPage"),
  "/medical-information": () => import("./pages/MedicalInformationPage"),
  "/treatment-summary": () => import("./pages/TreatmentSummaryPage"),
  "/patient-appointments": () => import("./pages/PatientAppointments"),
  "/medication-refills": () => import("./pages/MedicationRefillsPage"),
  "/subscription": () => import("./pages/SubscriptionPage"),
};

/** Destinations that mean a different page per portal — see the ByRole entries in router.tsx. */
const BY_AUDIENCE: Record<string, Partial<Record<Audience, Loader>>> = {
  "/dashboard": {
    clinical: () => import("./components/Dashboard/Internal/MtcDashboard"),
    patient: () => import("./components/Dashboard/Patient/PatientDashboard"),
    external: () => import("./components/Dashboard/External/ExternalDashboard"),
  },
  "/messages": {
    clinical: () => import("./components/Messages/Messages"),
    // The page, not the conversation component inside it — the page is what declares the data.
    patient: () => import("./pages/PatientMessagesPage"),
    external: () => import("./components/Messages/Messages"),
  },
  "/account": {
    clinical: () => import("./components/Profile/ProfilePage"),
    patient: () => import("./components/Profile/ProfilePage"),
    external: () => import("./components/ExternalProvider/ProviderSettings"),
  },
  "/patients": {
    clinical: () => import("./components/Patients/Patients"),
    external: () => import("./components/ExternalProviders/ExternalPatients"),
  },
};

/** Destinations already warmed this session, so re-hovering a link costs nothing. */
const warmed = new Set<string>();

/**
 * Warm `path` for `audience`. Safe to call on every hover: it dedupes, and it never throws —
 * a prefetch that fails is invisible, and the real navigation will surface the error properly.
 *
 * Returns whether this call started warming anything (false when the path is unmapped, when this
 * audience can't reach it, or when it was already warmed). Callers can ignore it; it exists so
 * the behavior is observable in tests.
 */
export function prefetchNavDestination(
  path: string,
  audience: Audience | null,
  ctx: PageDataContext = {},
): boolean {
  const key = `${audience ?? "-"}:${path}`;
  if (warmed.has(key)) return false;

  const loader = (audience ? BY_AUDIENCE[path]?.[audience] : undefined) ?? SHARED[path];
  if (!loader) return false;

  warmed.add(key);
  loader()
    .then((mod) => prefetchPageData(queryClient, mod, ctx))
    .catch(() => {
      // Offline, or a stale chunk after a deploy. Forget it so the next hover tries again.
      warmed.delete(key);
    });

  return true;
}

/** Test seam — the dedupe set is module state that would otherwise leak between cases. */
export function resetNavPrefetchCache() {
  warmed.clear();
}
